import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/Button";
import { Input } from "../ui/input";
import Label from "../ui/label";
import { X, Loader2, CheckCircle, AlertCircle, Building2, Globe, CreditCard } from "lucide-react";
import { usePaymentMethods } from "../../contexts/PaymentMethodsContext";

interface BankAccountFormProps {
  onClose: () => void;
  onSuccess: () => void;
  existingMethod?: any | null;
}

type BankTransferType = "sepa" | "international";

const BankAccountForm: React.FC<BankAccountFormProps> = ({
  onClose,
  onSuccess,
  existingMethod = null
}) => {
  const { addPaymentMethod, updatePaymentMethod } = usePaymentMethods();
  const [transferType, setTransferType] = useState<BankTransferType>(
    existingMethod?.type === "sepa_bank_account" ? "sepa" : "international"
  );
  const [formData, setFormData] = useState({
    accountHolderName: existingMethod?.accountHolderName || "",
    bankName: existingMethod?.bankName || "",
    accountNumber: existingMethod?.accountNumber || "",
    routingNumber: existingMethod?.routingNumber || "",
    iban: existingMethod?.iban || "",
    bic: existingMethod?.bic || "",
    swift: existingMethod?.swift || "",
    bankCountry: existingMethod?.bankCountry || "",
    accountType: existingMethod?.accountType || "personal",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    document.body.style.overflow = "hidden";
    
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleClose = () => {
    setSuccess(false);
    setError("");
    setLoading(false);
    document.body.style.overflow = "unset";
    onClose();
  };

  if (!mounted) return null;

  const validateForm = () => {
    if (!formData.accountHolderName || !formData.bankName) {
      setError("Please fill in all required fields");
      return false;
    }

    if (transferType === "sepa") {
      if (!formData.iban || !formData.bic) {
        setError("Please fill in IBAN and BIC/SWIFT for SEPA transfers");
        return false;
      }
    } else {
      if (!formData.accountNumber || !formData.routingNumber || !formData.swift || !formData.bankCountry) {
        setError("Please fill in all required fields for international transfers");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!validateForm()) {
        return;
      }

      const paymentMethodData: any = {
        type: transferType === "sepa" ? "sepa_bank_account" : "bank_account",
        bankName: formData.bankName,
        accountHolderName: formData.accountHolderName,
        accountType: formData.accountType,
        isDefault: false,
        metadata: {
          transferType,
          accountType: formData.accountType,
        }
      };

      // Add type-specific fields
      if (transferType === "sepa") {
        paymentMethodData.iban = formData.iban;
        paymentMethodData.bic = formData.bic;
      } else {
        paymentMethodData.accountNumber = formData.accountNumber;
        paymentMethodData.routingNumber = formData.routingNumber;
        paymentMethodData.swift = formData.swift;
        paymentMethodData.bankCountry = formData.bankCountry;
      }

      if (existingMethod) {
        // Update existing method
        await updatePaymentMethod(existingMethod.id, {
          metadata: {
            ...existingMethod.metadata,
            ...paymentMethodData.metadata,
            accountHolderName: formData.accountHolderName,
            bankName: formData.bankName,
            accountType: formData.accountType,
            transferType,
          }
        });
      } else {
        // Create new method
        await addPaymentMethod(paymentMethodData);
      }
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || "Failed to save bank account");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError("");
  };

  const handleTransferTypeChange = (type: BankTransferType) => {
    setTransferType(type);
    setError("");
  };

  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      style={{ backdropFilter: "blur(4px)" }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {existingMethod ? "Update" : "Add"} Bank Account
                </h2>
                <p className="text-blue-100 text-sm">Add your bank account details</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center py-8"
            >
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Bank Account {existingMethod ? "Updated" : "Added"} Successfully!
              </h3>
              <p className="text-gray-600">
                Your bank account details have been saved successfully.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-50 border border-red-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-red-800">Error</h4>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Transfer Type Selection */}
              <div>
                <Label className="block text-sm font-medium text-gray-700 mb-3">
                  Bank Transfer Type <span className="text-red-500">*</span>
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleTransferTypeChange("sepa")}
                    className={`relative flex items-start gap-3 p-4 border-2 rounded-xl transition-all ${
                      transferType === "sepa"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <div className={`flex-shrink-0 mt-0.5 ${
                      transferType === "sepa" ? "text-blue-600" : "text-gray-400"
                    }`}>
                      <CreditCard className="h-5 w-5" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">European (SEPA) Bank Transfer</span>
                        {transferType === "sepa" && (
                          <CheckCircle className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">For European bank accounts</p>
                    </div>
                    {transferType === "sepa" && (
                      <div className="absolute top-2 right-2">
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      </div>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTransferTypeChange("international")}
                    className={`relative flex items-start gap-3 p-4 border-2 rounded-xl transition-all ${
                      transferType === "international"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <div className={`flex-shrink-0 mt-0.5 ${
                      transferType === "international" ? "text-blue-600" : "text-gray-400"
                    }`}>
                      <Globe className="h-5 w-5" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">Extra-European / International Bank Transfer</span>
                        {transferType === "international" && (
                          <CheckCircle className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">For international bank accounts</p>
                    </div>
                    {transferType === "international" && (
                      <div className="absolute top-2 right-2">
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      </div>
                    )}
                  </button>
                </div>
              </div>

              {/* Account Holder Name */}
              <div>
                <Label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700 mb-2">
                  Account Holder Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="accountHolderName"
                  name="accountHolderName"
                  type="text"
                  value={formData.accountHolderName}
                  onChange={handleInputChange}
                  placeholder="e.g. John Doe"
                  required
                  className="w-full"
                  disabled={loading}
                />
              </div>

              {/* Bank Name */}
              <div>
                <Label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="bankName"
                  name="bankName"
                  type="text"
                  value={formData.bankName}
                  onChange={handleInputChange}
                  placeholder={transferType === "sepa" ? "e.g. Deutsche Bank" : "e.g. Bank of America"}
                  required
                  className="w-full"
                  disabled={loading}
                />
              </div>

              {/* SEPA Fields */}
              {transferType === "sepa" && (
                <>
                  <div>
                    <Label htmlFor="iban" className="block text-sm font-medium text-gray-700 mb-2">
                      IBAN <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="iban"
                      name="iban"
                      type="text"
                      value={formData.iban}
                      onChange={handleInputChange}
                      placeholder="e.g. DE89370400440532013000"
                      required
                      className="w-full"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="bic" className="block text-sm font-medium text-gray-700 mb-2">
                      BIC / SWIFT <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="bic"
                      name="bic"
                      type="text"
                      value={formData.bic}
                      onChange={handleInputChange}
                      placeholder="e.g. DEUTDEDBFRA"
                      required
                      className="w-full"
                      disabled={loading}
                    />
                  </div>
                </>
              )}

              {/* International Fields */}
              {transferType === "international" && (
                <>
                  <div>
                    <Label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-2">
                      Account Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="accountNumber"
                      name="accountNumber"
                      type="text"
                      value={formData.accountNumber}
                      onChange={handleInputChange}
                      placeholder="e.g. 1234567890"
                      required
                      className="w-full"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="routingNumber" className="block text-sm font-medium text-gray-700 mb-2">
                      Routing / Transit / Institution Code <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="routingNumber"
                      name="routingNumber"
                      type="text"
                      value={formData.routingNumber}
                      onChange={handleInputChange}
                      placeholder="e.g. 021000021 (US) or 00011-003 (Canada)"
                      required
                      className="w-full"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="swift" className="block text-sm font-medium text-gray-700 mb-2">
                      SWIFT / BIC <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="swift"
                      name="swift"
                      type="text"
                      value={formData.swift}
                      onChange={handleInputChange}
                      placeholder="e.g. BOFAUS3N"
                      required
                      className="w-full"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="bankCountry" className="block text-sm font-medium text-gray-700 mb-2">
                      Bank Country <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="bankCountry"
                      name="bankCountry"
                      type="text"
                      value={formData.bankCountry}
                      onChange={handleInputChange}
                      placeholder="e.g. United States"
                      required
                      className="w-full"
                      disabled={loading}
                    />
                  </div>
                </>
              )}

              {/* Account Type */}
              <div>
                <Label htmlFor="accountType" className="block text-sm font-medium text-gray-700 mb-2">
                  Account Type
                </Label>
                <select
                  id="accountType"
                  name="accountType"
                  value={formData.accountType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loading}
                >
                  <option value="">-- Select Type --</option>
                  <option value="personal">Personal</option>
                  <option value="business">Business</option>
                </select>
              </div>

              {/* Information Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">Secure Storage</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Your bank account information is encrypted and stored securely.
                      This information will be used for manual payment processing.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1 h-12"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {existingMethod ? "Updating..." : "Saving..."}
                    </>
                  ) : (
                    <>
                      <Building2 className="h-4 w-4 mr-2" />
                      {existingMethod ? "Update" : "Add"} Bank Account
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default BankAccountForm;
